"use client";

import React, { useState } from 'react';
import { usePathname } from 'next/navigation';
import { NavRail } from '@/components/navigation/nav-rail';
import { WorkspaceSwitcherCompact } from '@/components/navigation/workspace-switcher';
import { Home, Database, Table2, ArrowLeftRight, History, MessageSquare, Sun, Moon, User } from 'lucide-react';
import { UserDropdown } from '@/components/auth/user-dropdown';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { createClient } from '@/lib/supabase/client';
import { useTheme } from "next-themes";
import { AuthButtons } from '@/components/auth/auth-buttons';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UpgradeModal } from '@/components/subscription/upgrade-modal';
import { trackAction } from '@/lib/tracking';
import { useUser } from '@/hooks/use-user';
import { ChangelogModal } from '@/components/changelog/changelog-modal';
import { useSubscription } from '@/hooks/use-subscription';
import { toast } from '@/components/ui';
import { getAppNavigationConfig } from '@/lib/navigation/nav-config';

export function AppNavRail() {
  const pathname = usePathname();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { user, isLoading } = useUser();
  const { hasSubscription, subscriptionDetails, redirectToBilling } = useSubscription();
  const supabase = createClient();
  const { theme, setTheme } = useTheme();
  
  // State for the upgrade modal
  const [upgradeModal, setUpgradeModal] = React.useState<{
    openTrigger: 'chat_suggestion' | 'upgrade_button' | 'pricing_card' | 'signin_button'
  } | null>(null);

  // State for changelog modal
  const [changelogModalOpen, setChangelogModalOpen] = React.useState(false);
  
  // State for billing loading
  const [isLoadingBilling, setIsLoadingBilling] = React.useState(false);

  // Handle user actions
  const handleChangelogClick = () => {
    setChangelogModalOpen(true);
  }

  const handleThemeToggle = () => {
    setTheme(theme === "light" ? "dark" : "light");
  }

  const handleUpgradeClick = () => {
    setUpgradeModal({ openTrigger: 'signin_button' });
    trackAction('upgrade_modal_triggered', {
      source: 'nav_rail',
      trigger: 'signin_button'
    });
  }

  // Custom theme icon component that matches nav rail style
  const ThemeIcon = () => (
    <>
      <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
    </>
  );

  // Helper functions for user management
  const handleBillingPortal = async () => {
    setIsLoadingBilling(true);
    try {
      await redirectToBilling();
    } catch (error) {
      console.error('Error accessing billing portal:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to access billing portal');
    } finally {
      setIsLoadingBilling(false);
    }
  };

  // Create UserDropdown component or login avatar
  const userComponent = isLoading ? (
    <div className="w-8 h-8 rounded-full bg-muted animate-pulse"></div>
  ) : user ? (
    <UserDropdown />
  ) : (
    <button onClick={handleUpgradeClick}>
      <Avatar className="h-8 w-8 cursor-pointer hover:ring-2 hover:ring-ring transition-all">
        <AvatarFallback className="bg-muted hover:bg-muted/80">
          <User className="h-4 w-4" />
        </AvatarFallback>
      </Avatar>
    </button>
  );

  // Get navigation configuration
  const navigationConfig = getAppNavigationConfig({
    onChangelogClick: handleChangelogClick,
    onThemeToggle: handleThemeToggle,
    userDropdown: userComponent,
    themeIcon: <ThemeIcon />
  });

  // Determine active item based on current path
  const getActiveId = () => {
    if (pathname.startsWith('/home')) return 'home';
    if (pathname.startsWith('/datasets')) return 'datasets';
    if (pathname.startsWith('/app')) return 'view';
    if (pathname.startsWith('/convert')) return 'convert';
    return 'home'; // Default to home
  };

  return (
    <>
      {/* Desktop Navigation Rail */}
      <div className="hidden md:block">
        <NavRail
          config={navigationConfig}
          defaultActiveId={getActiveId()}
        />
      </div>

      {/* Mobile Navigation - keeping existing mobile implementation for now */}
      <div className="md:hidden">
        <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="m-2">
              <Database className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <div className="flex flex-col h-full">
              <div className="p-4 border-b">
                <h2 className="font-semibold text-lg">Navigation</h2>
              </div>
              
              <div className="flex-1 overflow-y-auto p-4">
                <nav className="space-y-2">
                  {navigationConfig.primary.map((item) => (
                    <Link
                      key={item.id}
                      href={item.href}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                        pathname.startsWith(item.href)
                          ? 'bg-primary text-primary-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                      )}
                      onClick={() => setMobileNavOpen(false)}
                    >
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  ))}
                </nav>
              </div>
              
              <div className="p-4 border-t">
                <AuthButtons />
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
      
      {/* Upgrade Modal */}
      <UpgradeModal
        open={upgradeModal !== null}
        onClose={() => setUpgradeModal(null)}
        openTrigger={upgradeModal?.openTrigger}
      />
      
      {/* Changelog Modal */}
      <ChangelogModal 
        open={changelogModalOpen} 
        onOpenChange={setChangelogModalOpen}
      />
    </>
  );
} 